import React, { useContext, useEffect, useState } from "react";
import {
  Close,
  DarkMode,
  Download,
  LightMode,
  Menu,
} from "@mui/icons-material";
import { ThemeContext } from "./ThemeContext";
import "./SiteNavigation.css";
import WeatherPanel from "./WeatherPanel";
import { logResumeClick } from "../utils/eventLogger";

const navigationItems = [
  { label: "Work", href: "#experience", id: "experience" },
  { label: "Research", href: "#research", id: "research" },
  { label: "Projects", href: "#projects", id: "projects" },
  { label: "Tools", href: "#skills", id: "skills" },
  { label: "Recommendations", href: "#testimonials", id: "testimonials" },
  { label: "Contact", href: "#contact", id: "contact" },
];

const journeyItems = [
  { label: "Arrival", id: "home" },
  ...navigationItems,
];

const SiteNavigation = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const observedIds = ["home", ...navigationItems.map((item) => item.id)];
    let frame = 0;
    const updateActiveSection = () => {
      frame = 0;
      const probe = 72 + window.innerHeight * 0.24;
      const sections = observedIds.map((id) => document.getElementById(id)).filter(Boolean);
      const active = sections.find((section) => {
        const rect = section.getBoundingClientRect();
        return rect.top <= probe && rect.bottom > probe;
      }) || sections.reduce((nearest, section) => {
        const distance = Math.abs(section.getBoundingClientRect().top - probe);
        return !nearest || distance < nearest.distance ? { section, distance } : nearest;
      }, null)?.section;
      if (active) setActiveSection(active.id);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(updateActiveSection);
    };
    updateActiveSection();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);
  const activeIndex = Math.max(0, journeyItems.findIndex((item) => item.id === activeSection));

  return (
    <header className="site-navigation" style={{ "--journey-progress": `${(activeIndex / (journeyItems.length - 1)) * 100}%` }}>
      <a className="site-wordmark" href="#home" aria-label="Aviral Kumar Srivastava home" onClick={closeMenu}>
        Aviral
      </a>

      <nav className={`site-links ${menuOpen ? "is-open" : ""}`} aria-label="Main navigation">
        {navigationItems.map((item) => (
          <a
            key={item.id}
            href={item.href}
            className={activeSection === item.id ? "is-active" : ""}
            onClick={() => { setActiveSection(item.id); closeMenu(); }}
          >
            {item.label}
          </a>
        ))}
      </nav>

      <div className="site-actions">
        <WeatherPanel />
        <button type="button" onClick={toggleTheme} aria-label="Toggle color theme" title="Toggle color theme">
          {theme === "light" ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
        </button>
        <a
          className="resume-link"
          href="https://drive.google.com/file/d/1xJzweqCZnWiUBFgawETXBifYT2GuLokp/view?usp=drive_link"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open resume"
          title="Resume"
          onClick={logResumeClick}
        >
          <Download fontSize="small" />
          <span>Resume</span>
        </a>
        <button
          type="button"
          className="menu-toggle"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <Close fontSize="small" /> : <Menu fontSize="small" />}
        </button>
      </div>

      <div className="journey-progress" aria-hidden="true">
        <span className="journey-progress-track"><span /></span>
        <span className="journey-progress-coordinate">{String(activeIndex + 1).padStart(2, "0")} / {String(journeyItems.length).padStart(2, "0")}</span>
      </div>
    </header>
  );
};

export default SiteNavigation;
